// Profile uploads panel — PivotPhase-3 attachments.
// Government-issued photo ID (front + back). W-9 collection is handled offline by Omertàbella ops.
// 2026-07-13 — 5-image `content_sample` block removed per product cut. The backend
// `profile_uploads.py` still accepts / lists / streams `content_sample` files so
// admins can view legacy uploads on old creator files; the creator-facing UI just
// no longer surfaces the panel or the upload affordance.
import { useEffect, useRef, useState } from "react";
import { apiClient, API } from "../../auth/AuthContext";

const SINGLE_KINDS = [
  { kind: "id_front", label: "Government-issued photo ID · Front" },
  { kind: "id_back",  label: "Government-issued photo ID · Back" },
];

function bytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function SingleSlot({ slot, file, onUpload, onDelete }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pick = () => inputRef.current?.click();
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(null);
    setBusy(true);
    try {
      await onUpload(slot.kind, f);
    } catch (e2) {
      setErr(String(e2?.response?.data?.detail || e2.message));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div
      data-testid={`upload-slot-${slot.kind}`}
      className="rounded border border-brand-text/15 bg-[#141414] p-4"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-[0.24em] text-brand-gold">
          {slot.label}
        </p>
        <span
          className={`text-[10px] uppercase tracking-[0.22em] ${
            file ? "text-emerald-400" : "text-brand-text/40"
          }`}
        >
          {file ? "On file" : "Missing"}
        </span>
      </div>
      {file ? (
        <div className="mt-3 text-xs text-brand-text/70">
          <div className="truncate">{file.filename}</div>
          <div className="mt-1 text-[10px] text-brand-text/40">
            {bytes(file.size_bytes)} · uploaded{" "}
            {new Date(file.uploaded_at).toLocaleDateString()}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-xs text-brand-text/50">
          PDF or image — kept strictly confidential.
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*,application/pdf"
        onChange={onPick}
        data-testid={`upload-input-${slot.kind}`}
      />
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          data-testid={`upload-pick-${slot.kind}`}
          onClick={pick}
          disabled={busy}
          className="rounded border border-brand-gold px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-gold hover:bg-brand-gold hover:text-brand-bg disabled:opacity-40"
        >
          {busy ? "Uploading…" : file ? "Replace" : "Upload"}
        </button>
        {file && (
          <>
            <a
              data-testid={`upload-view-${slot.kind}`}
              href={`${API}/me/profile/uploads/${file.file_id}`}
              target="_blank"
              rel="noreferrer"
              className="rounded border border-brand-text/20 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-text/70 hover:border-brand-gold/40 hover:text-brand-gold"
            >
              View
            </a>
            <button
              data-testid={`upload-delete-${slot.kind}`}
              onClick={() => onDelete(file.file_id)}
              className="rounded border border-rose-400/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-rose-300 hover:bg-rose-400/10"
            >
              Remove
            </button>
          </>
        )}
      </div>
      {err && (
        <p data-testid={`upload-error-${slot.kind}`} className="mt-2 text-[11px] text-rose-300">
          {err}
        </p>
      )}
    </div>
  );
}

export default function UploadsPanel() {
  const [items, setItems] = useState([]);

  const load = async () => {
    const r = await apiClient.get("/me/profile/uploads");
    setItems(r.data.items || []);
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    await apiClient.post(`/me/profile/uploads/${kind}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await load();
  };

  const del = async (fileId) => {
    await apiClient.delete(`/me/profile/uploads/${fileId}`);
    await load();
  };

  const byKind = (k) => items.find((it) => it.kind === k);

  return (
    <section
      data-testid="profile-uploads-panel"
      className="mt-10 rounded border border-brand-gold/20 bg-[#1A1A1A] p-6 sm:p-8"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-brand-gold">
            Identity Verification
          </p>
          <h3 className="mt-1 font-display text-2xl text-brand-text">
            Government-issued ID.
          </h3>
        </div>
      </div>
      <p className="mt-2 text-sm text-brand-text/60">
        For age + identity verification. Stored encrypted. Never shared.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SINGLE_KINDS.map((s) => (
          <SingleSlot
            key={s.kind}
            slot={s}
            file={byKind(s.kind)}
            onUpload={upload}
            onDelete={del}
          />
        ))}
      </div>
    </section>
  );
}
